import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { NumberUtilities } from '../../utilities/number-utilities';
import { ValueHelperModel } from '../low-level/value-helper.model';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { TimeModelOptions } from '../../models/options/time-model-options';
import { CodeModel, CodeModelFromJson, JsonCodeModel } from './code.model';
import { CrmCodeModel } from './crm-code.model';
import { DropdownModel } from './dropdown.model';
import {
    ODataTimeRecordingEntryOutModel,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from './time-recording-entry.model';
import { isDefined } from '../../utilities/is-defined';

export class TimeRecordingGeneralEntryModel extends TimeRecordingEntryModel {
    public typeOfWorkModel: DropdownModel<TextModel>;
    public billableModel: DropdownModel<TextModel>;
    public activitiesModel: DropdownModel<TextModel>;

    private activityCodes: CodeModel[];
    private modelOptionsLoader: ModelOptionsLoaderService;
    private timeRecordingBillableCodes: ValueHelperModel[];
    private timeRecordingTaskTypeInfoCodes: ValueHelperModel[];
    private phoneFixIndicatorAA: boolean;
    public isDurationUnchanged: boolean;

    public constructor(
        startDateTime: moment.Moment,
        endDateTime: moment.Moment,
        typeOfWork: TIMERECORDING_TYPE_OF_WORK_CODES | undefined,
        billableCode: ConfigConstants.BILLABLE_CODE | undefined,
        activityCode: string,
        activityCodes: CodeModel[],
        timeRecordingBillableCodes: ValueHelperModel[],
        timeRecordingTaskTypeInfoCodes: ValueHelperModel[],
        phoneFixIndicatorAA: boolean,
        isDurationUnchanged = true,
    ) {
        super('TimeRecordingGeneralEntryModel');
        const injector = CustomInjector.getInjector();
        const clientManager = injector.get(ClientManagerInstanceToken);
        this.id = moment().valueOf() + NumberUtilities.getRandomNumberBetweenMinAndMax();
        this.activityCodes = activityCodes;
        this.modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        this.timeRecordingBillableCodes = timeRecordingBillableCodes;
        this.timeRecordingTaskTypeInfoCodes = timeRecordingTaskTypeInfoCodes;
        this.isDurationUnchanged = isDurationUnchanged;

        const start = startDateTime.clone();

        // startDate and startTime
        this.startDateModel = <DateModel>this.set(new DateModel('StartDay', start));
        this.modelOptionsLoader.load(this.startDateModel, 'TimeRecording', 'StartDay');

        this.startTimeModel = <TimeModel>this.set(new TimeModel('StartTime', start));
        this.modelOptionsLoader.load(this.startTimeModel, 'TimeRecording', 'StartTime');

        if (clientManager.isNgmP58()) {
            // to avoid a desync for the startDateTime, we will store the current reference
            const currentStartDateTime = this.startDateTime;
            const tomorrowEndOfDay = moment().add(1, 'day').endOf('day');

            this.startDateModel = assignModelOptions(
                this.startDateModel,
                {
                    ...this.startDateModel.options,
                    maxValue: tomorrowEndOfDay,
                },
                this,
            );
            this.startTimeModel = assignModelOptions(
                this.startTimeModel,
                {
                    ...this.startTimeModel.options,
                    maxValue: tomorrowEndOfDay,
                },
                this,
            );

            // Due to new created references for both models, we will now restore the previous startDateTime
            this.startDateModel.value = currentStartDateTime;
            this.startTimeModel.value = currentStartDateTime;
        }
        const clonedEndDateTime = endDateTime.clone();
        this.endDateModel = <DateModel>this.set(new DateModel('EndDay', clonedEndDateTime));
        const isRemoteWork = timeRecordingTaskTypeInfoCodes.some((task) =>
            this.isRemoteWork(<TIMERECORDING_TYPE_OF_WORK_CODES>task.code),
        );
        const endTime = this.isDurationUnchanged && isRemoteWork ? moment.invalid() : clonedEndDateTime;
        this.endTimeModel = <TimeModel>this.set(new TimeModel('EndTime', endTime, new TimeModelOptions()));

        // Type Of Work
        const filteredValueHelper = timeRecordingTaskTypeInfoCodes.filter((vh: ValueHelperModel) => {
            return (
                vh.code !== TIMERECORDING_TYPE_OF_WORK_CODES.TRTO &&
                vh.code !== TIMERECORDING_TYPE_OF_WORK_CODES.TRBK &&
                vh.code !== TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY &&
                vh.code !== TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY
            );
        });
        const typeOfWorkEntries = filteredValueHelper.map((vh: ValueHelperModel) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel('WorkCodeEntry', vh.code));
        });
        if (phoneFixIndicatorAA) {
            typeOfWorkEntries.push(new DisplayDecorator<TextModel>('Phone Fix', new TextModel('WorkCodeEntry', 'PFI')));
        }
        this.typeOfWorkModel = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('WorkCode', typeOfWorkEntries))
        );
        this.typeOfWorkModel.selectByPredicate((typeOfWorkEntry) => {
            return typeOfWorkEntry.model.value === typeOfWork;
        });
        this.modelOptionsLoader.load(this.typeOfWorkModel, 'TimeRecording', 'WorkCode');

        // Activities
        const activitiesHelper = activityCodes.map((codeModel: CodeModel) => {
            return new DisplayDecorator<TextModel>(
                codeModel.codeDesc.value,
                new TextModel('CodeEntry', codeModel.codeName.value),
            );
        });
        this.activitiesModel = new DropdownModel<TextModel>('Code', activitiesHelper);
        if (activityCode) {
            this.activitiesModel.selectByPredicate((activitiesEntry) => {
                return activitiesEntry.model.value === activityCode;
            });
        }
        this.modelOptionsLoader.load(this.activitiesModel, 'TimeRecording', 'ActivityCode');

        // Billable
        const billableEntries = timeRecordingBillableCodes.map((vh: ValueHelperModel) => {
            return new DisplayDecorator<TextModel>(vh.descText, new TextModel('BillableEntry', vh.code));
        });
        this.billableModel = <DropdownModel<TextModel>>(
            this.set(new DropdownModel<TextModel>('Billable', billableEntries))
        );
        this.billableModel.selectByPredicate((billableEntry) => {
            return billableEntry.model.value === billableCode;
        });
        this.billableModel = assignModelOptions(
            this.billableModel,
            {
                ...this.billableModel.options,
            },
            this,
        );
        this.modelOptionsLoader.load(this.billableModel, 'TimeRecording', 'Billable');
    }

    public setIsDurationUnchanged(isDurationUnchanged: boolean): void {
        this.isDurationUnchanged = isDurationUnchanged;
    }

    public getTypeOfWorkCode(): TIMERECORDING_TYPE_OF_WORK_CODES | undefined {
        return this.typeOfWorkModel?.selectedEntry
            ? <TIMERECORDING_TYPE_OF_WORK_CODES>this.typeOfWorkModel.selectedEntry.value
            : undefined;
    }

    public isRemoteWork(typeOfWork: TIMERECORDING_TYPE_OF_WORK_CODES | undefined): boolean {
        return (
            typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.FCC ||
            typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.ROSR ||
            typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.FCCN ||
            typeOfWork === TIMERECORDING_TYPE_OF_WORK_CODES.ROSN
        );
    }

    public disableBillableDropdown(): void {
        this.billableModel = assignModelOptions(
            this.billableModel,
            {
                ...this.billableModel.options,
                disabled: true,
            },
            this,
        );
    }

    public enableBillableDropdown(): void {
        this.billableModel = assignModelOptions(
            this.billableModel,
            {
                ...this.billableModel.options,
                disabled: false,
            },
            this,
        );
    }

    public clone(): TimeRecordingGeneralEntryModel {
        const instance = new TimeRecordingGeneralEntryModel(
            this.startDateTime,
            this.endDateTime,
            this.typeOfWorkModel.selectedEntry
                ? <TIMERECORDING_TYPE_OF_WORK_CODES>this.typeOfWorkModel.selectedEntry.value
                : undefined,
            this.billableModel.selectedEntry
                ? <ConfigConstants.BILLABLE_CODE>this.billableModel.selectedEntry.value
                : undefined,
            this.activitiesModel.selectedEntry ? this.activitiesModel.selectedEntry.value : '',
            this.activityCodes,
            this.timeRecordingBillableCodes,
            this.timeRecordingTaskTypeInfoCodes,
            this.phoneFixIndicatorAA,
            this.isDurationUnchanged,
        );
        return instance;
    }

    public getActivityCodesModel(): CrmCodeModel {
        const selectedCodeArray = this.activityCodes.filter((codeModel: CodeModel) => {
            return codeModel.codeName.value === this.activitiesModel.selectedEntry.value;
        });
        if (selectedCodeArray.length !== 1) {
            throw new Error(
                'Outmodel could not be generated. Cannot find selected ActivityCode to add entries to CrmCodesSet for TimeRecording.',
            );
        }
        const selectedCode = selectedCodeArray[0];

        const crmCodeModel = CrmCodeModel.fromCodeModel(selectedCode);
        crmCodeModel.startDate.value = this.startDateTime.clone();
        crmCodeModel.startTime.value = this.startDateTime.clone();
        crmCodeModel.endDate.value = this.endDateTime.clone();
        crmCodeModel.endTime.value = this.endDateTime.clone();

        return crmCodeModel;
    }

    public getOutModel(): ODataTimeRecordingEntryOutModel {
        return super.getOutModel();
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): unknown {
        const jsonModel = {
            name: this.name,
            startDateTime: this.startDateModel.value,
            endDateTime: this.endDateModel.value,
            typeOfWork: this.typeOfWorkModel.selectedEntry ? this.typeOfWorkModel.selectedEntry.value : undefined,
            billableCode: this.billableModel.selectedEntry ? this.billableModel.selectedEntry.value : undefined,
            activityCode: this.activitiesModel.selectedEntry ? this.activitiesModel.selectedEntry.value : undefined,
            activityCodes: this.activityCodes,
            isDurationUnchanged: this.isDurationUnchanged,
        };

        return jsonModel;
    }

    public isPhoneFix(): boolean {
        const workCode = this.getTypeOfWorkCode();
        return (
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX ||
            workCode === TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES
        );
    }
}

export class TimeRecordingGeneralEntryModelFromJson extends TimeRecordingGeneralEntryModel {
    public constructor(
        data: JsonTimeRecordingGeneralEntryModel,
        timeRecordingBillableCodes: ValueHelperModel[],
        timeRecordingTaskTypeInfoCodes: ValueHelperModel[],
        phoneFixIndicatorAA: boolean,
    ) {
        data.isDurationUnchanged = isDefined(data.isDurationUnchanged) ? data.isDurationUnchanged : true;
        super(
            moment(data.startDateTime),
            moment(data.endDateTime),
            data.typeOfWork,
            data.billableCode,
            data.activityCode,
            data.activityCodes.map((code) => {
                return new CodeModelFromJson(code);
            }),
            timeRecordingBillableCodes,
            timeRecordingTaskTypeInfoCodes,
            phoneFixIndicatorAA,
            data.isDurationUnchanged,
        );
    }
}

export interface JsonTimeRecordingGeneralEntryModel {
    name: string;
    startDateTime: string;
    endDateTime: string;
    typeOfWork: TIMERECORDING_TYPE_OF_WORK_CODES;
    billableCode: ConfigConstants.BILLABLE_CODE;
    activityCode: string;
    activityCodes: JsonCodeModel[];
    isDurationUnchanged: boolean;
}

export function isTimeRecordingGeneralEntryModel(
    entry: TimeRecordingEntryModel,
): entry is TimeRecordingGeneralEntryModel {
    return entry.name === 'TimeRecordingGeneralEntryModel';
}
